/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'

import { Modal, ModalHeader, ModalBody, Col, Button, Row } from 'reactstrap'

import ReactTable from 'react-table-legacy'
import "react-table-legacy/react-table.css";

import { css } from '@emotion/react';
import { RingLoader } from 'react-spinners'

import {AppContext} from '../AppContext';

import prettyBytes from 'pretty-bytes';

class UserStreamingHistory extends React.Component
{
  debouncedSaveColumnWidths = null;
  columnKey = 'userGatewayHistory.columnWidths';
  columnWidths = {};
  sortColumnLS = 'userGatewayHistory.sortColumn';
  objectName = null;

  closeModal(event)
  {
    this.setState({isModalOpen: false});
  }  

  componentDidMount()
  {
    this.columnWidths = this.context.appLocalStorage.getColumnWidths(this.state.columns, this.columnKey);
    document.title = this.context.websiteProfile.profileName + ': User Gateway History'
    this.fetchUserGatewayHistory();

    let sortColumn = JSON.parse(this.context.appLocalStorage.getItem(this.sortColumnLS))
    if (null === sortColumn) sortColumn = undefined;

    this.setState({sortColumn});
  }

  constructor(props)
  {
    super();

    let columns = 
    [
      {Header: 'Object Name', id: 'objectName', accessor: 'objectName',
        Cell: row => <div><span title={row.value}>{row.value}</span></div>},
      {Header: 'Creation Date', id: 'creationDate', accessor: d => {return d.creationDate}, 
        Cell: rowInfo => (<div><span title={window.formatTimestamp(rowInfo.row.creationDate, true)}>{window.formatTimestamp(rowInfo.row.creationDate, false)}</span></div>)},
      {Header: 'Last Access', id: 'lastAccess', accessor: d => {return d.lastAccess}, 
        Cell: rowInfo => (<div><span title={window.formatTimestamp(rowInfo.row.lastAccess, true)}>{window.formatTimestamp(rowInfo.row.lastAccess, false)}</span></div>)},
      {Header: 'Access Mode', id: 'accessMode', accessor: d => { return d.accessMode},
        Cell: rowInfo =>(<div>{this.formatAccessMode(rowInfo.row.accessMode)}</div>)},
      {Header: 'Access Count', id: 'accessCount', accessor: 'accessCount'},
      {Header: 'Access Limit', id: 'accessLimit', accessor: d => { return d.accessLimit},
        Cell: rowInfo =>(<div>{this.formatAccessLimit(rowInfo.row.accessLimit)}</div>)},
      {Header: 'Access Detail', id: 'accessDetail', show: true,
        Cell: (rowInfo) => 
        { 
          return (
            <div className="text-center">
              <Button className="btn-sm" color="primary" onClick={this.viewAccessDetail.bind(this, rowInfo)}>Details</Button>
            </div>
          );
        }
      }
    ];

    let detailColumns =
    [
      {Header: 'Open Timestamp', id: 'openTimestamp', accessor: d => {return d.openTimestamp},
        Cell: rowInfo => (<div><span title={window.formatTimestamp(rowInfo.row.openTimestamp, true)}>{window.formatTimestamp(rowInfo.row.openTimestamp, false)}</span></div>)},
      {Header: 'Close Timestamp', id: 'closeTimestamp', accessor: d => {return d.closeTimestamp},
        Cell: rowInfo => (<div><span title={window.formatTimestamp(rowInfo.row.closeTimestamp, true)}>{window.formatTimestamp(rowInfo.row.closeTimestamp, false)}</span></div>)},
      {Header: 'Bytes Read', id: 'bytesRead', accessor: 'bytesRead',
        Cell: row => (<div><span title={new Intl.NumberFormat().format(parseInt(row.value, 10)) + ' Bytes'}>{prettyBytes(parseInt(row.value, 10))}</span></div>),
        sortMethod: (a, b, desc) =>
        {
          if (a > b)
            return 1;
          else
            return -1;
        }
      },
      {Header: 'Bytes Written', id: 'bytesWritten', accessor: 'bytesWritten',
        Cell: row => (<div><span title={new Intl.NumberFormat().format(parseInt(row.value, 10)) + ' Bytes'}>{prettyBytes(parseInt(row.value, 10))}</span></div>),
        sortMethod: (a, b, desc) =>
        {
          if (a > b)
            return 1;
          else
            return -1;
        }
      }
    ];

    this.debouncedSaveColumnWidths = window.debounce(this.saveColumnWidths, 500);
    this.fetchUserGatewayHistory = this.fetchUserGatewayHistory.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.sortChanged = this.sortChanged.bind(this);
    this.fetchAccessDetail = this.fetchAccessDetail.bind(this);

    this.state =
    {
      sortColumn: undefined,
      columns: columns,
      detailColumns: detailColumns,
      gatewayHistory: [],
      accessDetail: [],
      spinnerIsSpinning: false,
      isModalOpen: false
    }
  }

  async fetchAccessDetail(fileKey, objectName)
  {
    let bodyData = {}
    bodyData.fileKey = fileKey;

    this.objectName = objectName;

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'getAccessDetail', bodyData);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error retrieving access detail.');
    if (!result.jsonData.length)
    {
      this.context.postNotification('info', 'No access detail info available.');
      return;
    } 

    this.setState({isModalOpen: true, accessDetail: result.jsonData});
  }

  async fetchUserGatewayHistory()
  {
    this.setState({spinnerIsSpinning: true});

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'getUserGatewayHistory');
    this.setState({spinnerIsSpinning: false});
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error loading user gateway history data');
    this.setState({gatewayHistory: result.jsonData.userGatewayHistory});
  }

  formatAccessLimit(accessLimit)
  {
    switch (accessLimit)
    {
      case -1:
        return 'Unlimited';

      default:
        return accessLimit;
    }
  }

  formatAccessMode(accessMode)
  {
    switch (accessMode)
    {
      case 'R':
        return 'Read';

      case 'U':
        return 'Read/Write';

      case 'W':
        return 'Write';

      default:
        return 'Read';
    }
  }

  render()
  {
    const override = css`
      display: block;
      margin: 0 auto;
      border-color: red;
    `;

    return(
      <div>
        <Modal className='modal-md' isOpen={this.state.isModalOpen} toggle={this.closeModal} autoFocus={false} >
          <ModalHeader toggle={this.closeModal}>Gateway Access Details - {this.objectName}</ModalHeader>
          <ModalBody>
            <ReactTable columns={this.state.detailColumns} data={this.state.accessDetail} minRows={1} className="-striped -highlight" />
          </ModalBody>
        </Modal>
        <Row><h2>User Gateway History</h2></Row>
        <Row style={{paddingTop: "10px"}}>
          <Col>
            <ReactTable columns={this.state.columns} data={this.state.gatewayHistory} minRows={1} className="-striped -highlight" 
              onResizedChange={(resizedColumns, event) => this.debouncedSaveColumnWidths(resizedColumns, event)} 
              defaultSorted={this.state.sortColumn} onSortedChange={this.sortChanged} />
          </Col>
        </Row>
        <div style={{position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)"}}>
          <RingLoader css={override} size={75} color={'#4A90E2'} loading={this.state.spinnerIsSpinning}/>          
        </div>
      </div>
    );
  }

  saveColumnWidths(resizedColumns)
  {
    this.context.appLocalStorage.saveColumnWidths(resizedColumns, this.columnWidths, this.columnKey);
  }

  sortChanged(sortColumn)
  {
    this.context.appLocalStorage.setItem(this.sortColumnLS, JSON.stringify(sortColumn));
  }

  viewAccessDetail(rowInfo)
  {
    this.fetchAccessDetail(rowInfo.original.fileKey, rowInfo.original.objectName);
  }
}

UserStreamingHistory.contextType = AppContext;

export default UserStreamingHistory